import {
	ActionItemId,
	SuretyAccountStatus,
	SuretyBondStatus,
	SuretyQuoteStatus,
} from "@inrev/common";
import { ActionItem, useFetchAdminActionItems } from "@portal/api/action-item/admin";
import {
	useFetchAccountPreviews,
	useFetchBondPreviews,
	useFetchBondRequestPreviews,
} from "@portal/api/previews";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { match } from "ts-pattern";
import {
	formatSuretyBondDescription,
	formatSuretyQuoteDescription,
} from "../../../../utils/string";
import { type DashboardScope, scopeMatches } from "../scope";

export type ActionItemWithNesting = ActionItem & {
	entityTitle?: string;
	entityStatus?: SuretyAccountStatus | SuretyBondStatus | SuretyQuoteStatus;
	numOpenItems?: number;
	assigneeIds?: string[];
	subItems?: ActionItem[];
};

export const useActionItems = ({
	showComplete,
	scope,
}: {
	showComplete: boolean;
	scope: DashboardScope;
}) => {
	const { user } = useAuthenticatedUser();
	const [recentlyCompletedIds, setRecentlyCompletedIds] = useState<string[]>([]);
	const { actionItems, actionItemsIsLoading } = useFetchAdminActionItems();
	const { accountPreviews, accountPreviewsIsLoading } = useFetchAccountPreviews();
	const { bondPreviews, bondPreviewsIsLoading } = useFetchBondPreviews();
	const { bondRequestPreviews: quotes, bondRequestPreviewsIsLoading } =
		useFetchBondRequestPreviews();

	return useMemo(() => {
		const allActionItems: ActionItemWithNesting[] = [];
		const addRecentlyCompleted = (ids: string[]) => {
			setRecentlyCompletedIds((prevIds) => [...prevIds, ...ids]);
		};
		const removeRecentlyCompleted = (ids: string[]) => {
			setRecentlyCompletedIds((prevIds) => prevIds.filter((id) => !ids.includes(id)));
		};

		if (
			!actionItems ||
			actionItemsIsLoading ||
			accountPreviewsIsLoading ||
			bondPreviewsIsLoading ||
			bondRequestPreviewsIsLoading
		) {
			return {
				allActionItems,
				actionItemsIsLoading: true,
				addRecentlyCompleted,
				removeRecentlyCompleted,
			};
		}

		// Sort the action items by when they are due, with non-recent completed items at the end
		const sortedActionItems = Object.values(actionItems)
			.filter((item) =>
				showComplete ? true : !item.completedAt || recentlyCompletedIds.includes(item.id),
			)
			.filter((item) => scopeMatches(scope, item.assigneeId, user.id))
			.sort((a, b) => {
				// Only sort by completed status if we're showing completed items and they aren't recently completed
				if (showComplete) {
					const aCompleted = !!a.completedAt && !recentlyCompletedIds.includes(a.id);
					const bCompleted = !!b.completedAt && !recentlyCompletedIds.includes(b.id);

					if (aCompleted !== bCompleted) {
						return aCompleted ? 1 : -1;
					}
				}

				// If the action item doesn't have a due date, use the created date to sort
				const aEffectiveDate = a.dueAt ? a.dueAt : a.createdAt;
				const bEffectiveDate = b.dueAt ? b.dueAt : b.createdAt;

				return aEffectiveDate.toMillis() - bEffectiveDate.toMillis();
			});

		// Organize the action items by their relation, grouping them together
		const actionItemsMap: { [key: string]: ActionItemWithNesting } = {};

		sortedActionItems.forEach((actionItem) => {
			const groupKey = `${actionItem.relationType}_${actionItem.relationId}`;
			// If the group doesn't exist yet, create it
			if (!actionItemsMap[groupKey]) {
				// Create the object based on the relation type
				const parentActionItem = match(actionItem)
					.with({ relationType: "account" }, (actionItem) => {
						const account = accountPreviews?.find(
							(account) => account.id === actionItem.relationId,
						);

						if (account?.status === "closed" || account?.status === "declined") {
							return null;
						}

						return {
							...actionItem,
							entityTitle: account?.displayName ?? "Unknown Account",
							entityStatus: account?.status,
							assigneeIds: [],
							subItems: [],
						};
					})
					.with({ relationType: "bond" }, (actionItem) => {
						const bond = bondPreviews?.find((bond) => bond.id === actionItem.relationId);

						if (bond?.status === "closed") {
							return null;
						}

						return {
							...actionItem,
							entityTitle: formatSuretyBondDescription(bond),
							entityStatus: bond?.status,
							assigneeIds: [],
							subItems: [],
						};
					})
					.with({ relationType: "quote" }, (actionItem) => {
						const quote = quotes?.find((quote) => quote.id === actionItem.relationId);

						if (quote?.status === "closed" || quote?.status === "declined") {
							return null;
						}

						return {
							...actionItem,
							entityTitle: formatSuretyQuoteDescription(quote),
							entityStatus: quote?.status,
							assigneeIds: [],
							subItems: [],
						};
					})
					.exhaustive();

				// Early return before assignment to filter out closed items
				if (parentActionItem === null) {
					return;
				}

				actionItemsMap[groupKey] = {
					...parentActionItem,
					id: groupKey as ActionItemId,
					dueAt: actionItem.dueAt,
					title: "",
					content: "",
					status: actionItem.status,
				};
			}

			// We will always have sub items since we're grouping
			actionItemsMap[groupKey].subItems!.push(actionItem);
		});

		Object.values(actionItemsMap).forEach((actionItem) => {
			if (!actionItem.subItems) {
				return;
			}

			// If there are multiple sub-items, we need to set their entityTitle for display
			actionItem.subItems = actionItem.subItems.map((subItem) => ({
				...subItem,
				entityTitle: actionItem.entityTitle,
			}));

			// Set the completed count as title
			const completedCount = actionItem.subItems.filter(
				(item) => item.status === "complete",
			).length;
			actionItem.numOpenItems = actionItem.subItems.length - completedCount;

			// Collect unique assignee IDs
			actionItem.assigneeIds = Array.from(
				new Set(actionItem.subItems.flatMap((item) => item.assigneeId)),
			);

			// Set the most recent due date as the group due date filtering out completed items
			const mostRecentDueDate = actionItem.subItems.reduce(
				(latest, item) => {
					if (showComplete && !recentlyCompletedIds.includes(item.id) && item.completedAt) {
						return latest;
					}

					return item.dueAt && (!latest || item.dueAt.toMillis() < latest.toMillis())
						? item.dueAt
						: latest;
				},
				null as DateTime | null,
			);
			actionItem.dueAt = mostRecentDueDate;

			// Set the status based on if any of the items are open
			actionItem.status = actionItem.subItems.some((item) => item.status === "open")
				? "open"
				: "complete";

			allActionItems.push(actionItem);
		});

		return {
			allActionItems,
			actionItemsIsLoading: false,
			addRecentlyCompleted,
			removeRecentlyCompleted,
		};
	}, [
		actionItems,
		showComplete,
		scope,
		user.id,
		recentlyCompletedIds,
		bondPreviews,
		quotes,
		accountPreviews,
	]);
};
